import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  Renderer2,
  forwardRef,
  OnDestroy,
  inject
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Store } from '@ngrx/store';
import { debounce } from 'lodash-es';
import { MessageService } from 'primeng/api';
import { GlobalConstants } from 'src/app/base/global.constants';
import { UtilService } from 'src/app/services/util.service';
import { textareaConfig } from './vb-textarea.types';

@Component({
  selector: 'vb-textarea',
  templateUrl: './vb-textarea.component.html',
  styleUrls: ['./vb-textarea.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => VbTextareaComponent),
      multi: true
    }
  ],
  standalone: false //NOSONAR
})
export class VbTextareaComponent implements OnChanges, OnDestroy {
  private renderer = inject(Renderer2);
  private elementRef = inject(ElementRef);
  private store = inject(Store);
  private ms = inject(MessageService);
  private cd = inject(ChangeDetectorRef);
  public utilService = inject(UtilService);

  @Input() element: any;
  @Input() disableField = false;
  @Input() formData: any = null;
  @Input() currentSelectedTreeNode: any = null;
  @Output() public valChanges = new EventEmitter();
  txtAreaval: any;
  private modelValue: any;
  private onChange: (m: any) => void;
  private onTouched: (m: any) => void;

  get model() {
    return this.modelValue;
  }

  writeValue(value: any): void {
    this.modelValue = value;
    this.txtAreaval = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  set() {
    this.modelValue = this.txtAreaval;
    this.onChange(this.modelValue);
  }

  setDisabledState(isDisabled: boolean) {
    this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
    this.disableField = isDisabled;
  }

  debounceValChanges = debounce(this.set.bind(this), 1000);

  valueChange(event) {
    this.debounceValChanges();
  }

  ngOnChanges(): void {
    if (
      (this.formData?.f_sysF && this.formData?.name !== GlobalConstants.isActiveField) ||
      (this.formData?.purpose && this.formData?.purpose === 'REFERENCE')
    ) {
      this.disableField = true;
    }
  }

  ngOnDestroy(): void {
    //AutoUnsubscribe
    $(this.elementRef.nativeElement).remove();
  }

  getRowSize(): number {
    // Keep rich content areas tall enough to scroll instead of expanding endlessly.
    if (this.isInlineTextElement() || this.isHtmlContainerElement()) {
      return textareaConfig.inlineTextRowSize;
    }
    return textareaConfig.defaultRowSize;
  }

  getAutoResize(): boolean {
    // Disable autoResize for inline text and HTML container content so overflow scrolls.
    return !(this.isInlineTextElement() || this.isHtmlContainerElement());
  }

  private isInlineTextElement(): boolean {
    // Check the actual node type from currentSelectedTreeNode (FancyTree node)
    return this.currentSelectedTreeNode?.type === 'inlineText';
  }

  private isHtmlContainerElement(): boolean {
    return this.currentSelectedTreeNode?.type === 'htmlContainer';
  }
}
